import { defineComponent as _defineComponent } from 'vue';
import { unref as _unref, createVNode as _createVNode, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementBlock as _createElementBlock } from "vue";
const _hoisted_1 = {
    key: 0,
    class: "border-(--colors-border-base-soft) flex w-full items-center justify-between gap-5 border-b border-solid px-4 py-4",
    "data-testid": "navbar-contextual"
};
const _hoisted_2 = { class: "flex min-w-0 items-center gap-5" };
const _hoisted_3 = { class: "truncate text-2xl leading-[29px] font-bold text-(--colors-text-black-default) dark:text-(--colors-text-white-default)" };
const _hoisted_4 = {
    key: 0,
    class: "absolute top-full left-4 z-50 mt-1 w-[487px] max-w-[calc(100vw-2rem)]"
};
import { inject, ref, watch, computed, nextTick, onMounted, onUnmounted } from "vue";
import { SHELL_CONTRACTS_KEY } from "@/contracts/ShellContracts";
import { ShellEvent } from "@/contracts/types";
import { setLocale } from "@/plugins/i18n";
import { Navbar, Dropdown, Badge, Button } from "@eudonet/opal";
import { useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import { useProfileStore } from "@/modules/settings/public";
import { usePageHeaderStore } from "@/modules/header/stores/usePageHeader";
import { authState } from "@/plugins/keycloak";
import GlobalSearchResults from "./GlobalSearchResults.vue";
import { GLOBAL_SEARCH_LISTBOX_ID, globalSearchOptions, } from "./globalSearchOptions";
import { useGlobalSearch, GLOBAL_SEARCH_MIN_LENGTH, } from "@/modules/header/composables/useGlobalSearch";
import searchLineIcon from "remixicon/icons/System/search-line.svg?raw";
import userLine from "remixicon/icons/User & Faces/user-line.svg?url";
import logoutLine from "remixicon/icons/System/logout-box-r-line.svg?url";
export default /*@__PURE__*/ _defineComponent({
    __name: 'NavBar',
    setup(__props) {
        const router = useRouter();
        const { t } = useI18n();
        const profileStore = useProfileStore();
        const pageHeader = usePageHeaderStore();
        // ─── Global search (temporary OPQRY-backed quick search — placeholder until OPSEARCH) ───
        const { query, results, isLoading, error, clear } = useGlobalSearch();
        const isPanelOpen = ref(false);
        const searchRootRef = ref(null);
        const showResults = computed(() => isPanelOpen.value && query.value.trim().length >= GLOBAL_SEARCH_MIN_LENGTH);
        // ─── Combobox keyboard model (APG combobox with listbox popup); focus stays in the input ───
        const activeIndex = ref(-1);
        const options = computed(() => isLoading.value || error.value ? [] : globalSearchOptions(results.value));
        // Loading / error / empty show a status message, not a listbox: the combobox stays collapsed.
        const isListboxOpen = computed(() => showResults.value && options.value.length > 0);
        const activeOptionId = computed(() => isListboxOpen.value ? options.value[activeIndex.value]?.id : undefined);
        watch(options, () => {
            activeIndex.value = -1;
        });
        watch(activeOptionId, async (id) => {
            if (!id)
                return;
            await nextTick();
            document.getElementById(id)?.scrollIntoView({ block: "nearest" });
        });
        function onSearchKeydown(event) {
            if (event.isComposing)
                return;
            const last = options.value.length - 1;
            switch (event.key) {
                case "ArrowDown":
                case "ArrowUp":
                    openIfQuery();
                    if (!isListboxOpen.value)
                        return;
                    event.preventDefault();
                    // Clamped at both ends; ArrowUp with no active option starts from the last one.
                    activeIndex.value =
                        event.key === "ArrowDown"
                            ? Math.min(activeIndex.value + 1, last)
                            : activeIndex.value < 0
                                ? last
                                : Math.max(activeIndex.value - 1, 0);
                    return;
                case "Home":
                case "End":
                    // Only while an option is active; otherwise the keys move the caret in the input.
                    if (!activeOptionId.value)
                        return;
                    event.preventDefault();
                    activeIndex.value = event.key === "Home" ? 0 : last;
                    return;
                case "Enter": {
                    const option = isListboxOpen.value ? options.value[activeIndex.value] : undefined;
                    if (!option)
                        return;
                    event.preventDefault();
                    if (option.type === "result")
                        onSelect({ kind: option.kind, id: option.entityId });
                    else
                        onSeeAll(option.kind);
                    return;
                }
                case "Escape":
                    if (!showResults.value)
                        return;
                    // Consumed so SearchInput keeps the value and no outer overlay closes on the same press.
                    event.preventDefault();
                    event.stopPropagation();
                    closePanel();
                    return;
            }
        }
        function onSearchInput(value) {
            // Writing `query` drives the composable's debounced search via its watcher.
            query.value = value;
            isPanelOpen.value = value.trim().length >= GLOBAL_SEARCH_MIN_LENGTH;
            activeIndex.value = -1;
        }
        function openIfQuery() {
            if (query.value.trim().length >= GLOBAL_SEARCH_MIN_LENGTH) {
                isPanelOpen.value = true;
            }
        }
        function closePanel() {
            isPanelOpen.value = false;
            activeIndex.value = -1;
        }
        function onSelect(payload) {
            const routeName = payload.kind === "contact" ? "ContactDetail" : "Organisation";
            void router.push({ name: routeName, params: { id: payload.id } });
            closePanel();
            clear();
        }
        function onSeeAll(kind) {
            // Open the full list, pre-filtered by the query (the list seeds its search from `?q=`).
            const routeName = kind === "contact" ? "Contact" : "OrganisationList";
            const term = query.value.trim();
            void router.push({ name: routeName, query: { q: term } });
            closePanel();
            clear();
        }
        function handleSearchClickOutside(event) {
            if (searchRootRef.value && !searchRootRef.value.contains(event.target)) {
                closePanel();
            }
        }
        onMounted(() => document.addEventListener("mousedown", handleSearchClickOutside));
        onUnmounted(() => document.removeEventListener("mousedown", handleSearchClickOutside));
        const avatar = ref({
            name: "",
            email: "",
            img: undefined,
        });
        watch(() => authState.isAuthenticated, async (isAuthenticated) => {
            if (!isAuthenticated)
                return;
            try {
                const profile = await profileStore.fetchProfile();
                // Apply the user's server-side language as soon as we have it
                if (profile.displayLanguage) {
                    setLocale(profile.displayLanguage);
                }
                avatar.value = {
                    name: profile.fullName,
                    email: profile.email,
                    img: profile.profilePicture || undefined,
                };
            }
            catch {
                // keep empty defaults, avatar initials will be shown
            }
        }, { immediate: true });
        const avatarDropdownAriaLabel = computed(() => {
            const name = avatar.value.name?.trim() ?? "";
            return name ? t("navbar.userMenuNamed", { name }) : t("navbar.userMenu");
        });
        const avatarDropdownItems = computed(() => [
            {
                label: t("profile.title"),
                icon: userLine,
                onClick: () => router.push({ name: "ProfileSettings" }),
            },
            {
                label: t("logout.button"),
                icon: logoutLine,
                onClick: () => handleLogout(),
            },
        ]);
        // Get Shell Contracts through inject
        const contracts = inject(SHELL_CONTRACTS_KEY);
        if (!contracts) {
            throw new Error("ShellContracts not found. Make sure the module is properly registered.");
        }
        const handleProfileUpdated = (profile) => {
            if (!profile)
                return;
            avatar.value = {
                name: profile.fullName,
                email: profile.email,
                img: profile.profilePicture || undefined,
            };
        };
        onMounted(() => {
            contracts.eventBus.on(ShellEvent.USER_PROFILE_UPDATED, handleProfileUpdated);
        });
        onUnmounted(() => {
            contracts.eventBus.off(ShellEvent.USER_PROFILE_UPDATED, handleProfileUpdated);
        });
        const handleLogout = async () => {
            try {
                // Emit logout event (other modules can listen)
                contracts.eventBus.emit(ShellEvent.USER_LOGOUT);
                await contracts.auth.logout();
            }
            catch (error) {
                contracts.logger.debug("Logout failed:", error);
            }
        };
        return (_ctx, _cache) => {
            return (_unref(pageHeader).header)
                ? (_openBlock(), _createElementBlock("nav", _hoisted_1, [
                    _createElementVNode("div", _hoisted_2, [
                        _createVNode(_unref(Button), {
                            variant: "tertiary",
                            size: "sm",
                            "icon-left": "ri-arrow-left-line",
                            label: _unref(pageHeader).header.backLabel,
                            "data-testid": "navbar-back",
                            onClick: _cache[0] || (_cache[0] = ($event) => (_unref(pageHeader).header.onBack()))
                        }, null, 8 /* PROPS */, ["label"]),
                        _cache[1] || (_cache[1] = _createElementVNode("div", { class: "h-8 w-px shrink-0 bg-(--colors-border-base-medium)" }, null, -1 /* CACHED */)),
                        _createElementVNode("h1", _hoisted_3, _toDisplayString(_unref(pageHeader).header.title), 1 /* TEXT */),
                        (_unref(pageHeader).header.badge)
                            ? (_openBlock(), _createBlock(_unref(Badge), {
                                key: 0,
                                label: _unref(pageHeader).header.badge.label,
                                theme: _unref(pageHeader).header.badge.theme,
                                size: "sm",
                                "show-left-icon": false,
                                "show-close-icon": false
                            }, null, 8 /* PROPS */, ["label", "theme"]))
                            : _createCommentVNode("v-if", true)
                    ]),
                    _createVNode(_unref(Dropdown), {
                        avatar: avatar.value,
                        items: avatarDropdownItems.value,
                        "menu-align": "right",
                        "menu-width": "288px",
                        "aria-label": avatarDropdownAriaLabel.value
                    }, null, 8 /* PROPS */, ["avatar", "items", "aria-label"])
                ]))
                : (_openBlock(), _createElementBlock("div", {
                    key: 1,
                    ref_key: "searchRootRef",
                    ref: searchRootRef,
                    class: "relative"
                }, [
                    _createVNode(_unref(Navbar), {
                        "search-placeholder": _ctx.$t('navbar.searchPlaceholder'),
                        "search-label": _ctx.$t('navbar.searchPlaceholder'),
                        "search-controls": _unref(GLOBAL_SEARCH_LISTBOX_ID),
                        "search-expanded": isListboxOpen.value,
                        "search-activedescendant": activeOptionId.value,
                        "search-clear-label": _ctx.$t('common.clearSearch'),
                        "search-value": _unref(query),
                        avatar: avatar.value,
                        "avatar-dropdown-items": avatarDropdownItems.value,
                        "avatar-dropdown-aria-label": avatarDropdownAriaLabel.value,
                        "search-icon": _unref(searchLineIcon),
                        "onUpdate:searchValue": onSearchInput,
                        onSearchClick: openIfQuery,
                        onSearchKeydown: onSearchKeydown,
                        onSearchBlur: closePanel
                    }, null, 8 /* PROPS */, ["search-placeholder", "search-label", "search-controls", "search-expanded", "search-activedescendant", "search-clear-label", "search-value", "avatar", "avatar-dropdown-items", "avatar-dropdown-aria-label", "search-icon"]),
                    (showResults.value)
                        ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
                            _createVNode(GlobalSearchResults, {
                                results: _unref(results),
                                "is-loading": _unref(isLoading),
                                error: _unref(error),
                                query: _unref(query),
                                "active-option-id": activeOptionId.value,
                                onSelect: onSelect,
                                onSeeAll: onSeeAll
                            }, null, 8 /* PROPS */, ["results", "is-loading", "error", "query", "active-option-id"])
                        ]))
                        : _createCommentVNode("v-if", true)
                ], 512 /* NEED_PATCH */));
        };
    }
});
